"use client";

import { ChevronRight, ChevronDown, Edit, Trash2, Plus } from "lucide-react";
import { CategoryItem } from "../types";

interface CategoryRowProps {
  category: CategoryItem;
  isExpanded: boolean;
  isSelected: boolean;
  hasNestedItemsSupport: boolean;
  readOnlyCategory?: boolean;
  /** When true, hide the "Add Item" control (category-only forms such as Eligibility 2). */
  hideChildAdd?: boolean;
  /** When true, hide expand/collapse chevron (e.g. Visionaries: items live in pool, not under the row). */
  hideExpandToggle?: boolean;
  onToggleExpand: () => void;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddItem: () => void;
  onAddNestedItem?: () => void;
}

export const CategoryRow = ({
  category,
  isExpanded,
  isSelected,
  hasNestedItemsSupport,
  readOnlyCategory = false,
  hideChildAdd = false,
  hideExpandToggle = false,
  onToggleExpand,
  onClick,
  onEdit,
  onDelete,
  onAddItem,
  onAddNestedItem,
}: CategoryRowProps) => {
  const hasItems = category.items && category.items.length > 0;
  const hasSemesters = category.semesters && category.semesters.length > 0;
  const hasAnyChildren = hasItems || hasSemesters;

  return (
    <div
      className={`relative border rounded-lg p-3 transition-all group ${
        isSelected
          ? "bg-blue-50 border-blue-300 shadow-sm"
          : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
    >
      <div className="flex items-center gap-3">
        {!hideExpandToggle && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded flex-shrink-0 transition-colors"
          >
            {hasAnyChildren ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>
        )}

        {/* Category Icon */}
        {category.icon && <div className="flex-shrink-0 text-lg">{category.icon}</div>}

        {/* Category Content */}
        <div className="flex-1 flex items-center gap-2 min-w-0 cursor-pointer" onClick={onClick}>
          <span className={`font-semibold text-sm ${isSelected ? "text-blue-900" : "text-gray-900"}`}>
            {category.name?.trim() ||
              (readOnlyCategory || category.is_uncategorized_bucket || category.id === -1
                ? "Visionaries"
                : "")}
          </span>

          {/* Semester count badge */}
          {hasSemesters && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {category.semesters!.length} {category.semesters!.length === 1 ? "semester" : "semesters"}
            </span>
          )}

          {/* Item count badge */}
          {hasItems && !hasSemesters && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {category.items!.length} {category.items!.length === 1 ? "item" : "items"}
            </span>
          )}

          {typeof category.column_index === "number" && (
            <span className="text-xs bg-violet-100 text-violet-800 px-2 py-0.5 rounded">
              Col {category.column_index}
            </span>
          )}

          {/* Inactive badge */}
          {category.status === false && (
            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inactive</span>
          )}

          {/* Order index */}
          {category.order_index !== undefined && (
            <span className="text-xs text-gray-400">Order: {category.order_index || 1}</span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {hasNestedItemsSupport && onAddNestedItem && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddNestedItem();
              }}
              className="p-1.5 hover:bg-green-100 rounded text-green-600 transition-colors"
              title="Add Semester"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          {!hasNestedItemsSupport && !hideChildAdd && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddItem();
              }}
              className="p-1.5 hover:bg-green-100 rounded text-green-600 transition-colors"
              title="Add Item"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          {!readOnlyCategory && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-1.5 hover:bg-blue-100 rounded text-blue-600 transition-colors"
              title="Edit Category"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          {!readOnlyCategory && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 hover:bg-red-100 rounded text-red-600 transition-colors"
              title="Delete Category"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

